import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Cacheable } from 'ts-cacheable';
import { ConsentCaptureResponse } from 'src/app/document/model/consent-capture-response';
import { TokenCaptureLink } from 'src/app/document/model/token-capture-link';

@Injectable({
  providedIn: 'root'
})
export class DocumentService
{
  constructor(private http: HttpClient) { }

  @Cacheable()
  public retrieveCachedDocument(url: string): Observable<any>
  {
    return this.http.get<any>(url);
  }

  @Cacheable()
  public retrieveBrandImageDocument(): Observable<any>
  {
    return this.http.get<any>(`/documents/image/brand`);
  }

  /**
   * Return the proper class to used based on the file mime type.
   *
   * @param mimeType Mime Type of the file.
   * @returns String
   */
  public getFileTypeClass(mimeType: string): string
  {
    switch (mimeType)
    {
      // Word
      case 'application/msword':
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'fa-file-word';
      // Excel
      case 'application/vnd.ms-excel':
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return 'fa-file-excel';
      // Power Point
      case 'application/vnd.ms-powerpoint':
        return 'fa-file-powerpoint';
      // Pdf
      case 'application/pdf':
        return 'fa-file-pdf';
      // Image
      case 'image/jpeg':
      case 'image/png':
      case 'image/bmp':
        return 'fa-file-image';
      // Zip
      case 'application/x-zip-compressed':
        return 'fa-file-archive';
      // Video
      case 'video/avi':
      case 'video/quicktime':
      case 'video/mpeg':
        return 'fa-video';
      default:
        return 'fa-file-alt';
    }
  }

  public searchDocumentById(id: number): Observable<any>
  {
    // TODO: Change this, no need to rely on client side querying as the back end is ready with paging
    return this.http.get<any>(`/documents/${id}`);
  }

  public searchDocumentsByMember(memberId: number): Observable<any>
  {
    // TODO: Change this, no need to rely on client side querying as the back end is ready with paging
    return this.http.get<any>(`/documents?search=memberId:eq:${memberId}&size=100&sort=createdOn,desc`);
  }

  public searchDocumentsByCase(caseId: number): Observable<any>
  {
    // TODO: Change this, no need to rely on client side querying as the back end is ready with paging
    return this.http.get<any>(`/documents?search=caseId:eq:${caseId}&size=100&sort=createdOn,desc`);
  }

  public searchDocumentsByCaseAndTasks(caseId: number): Observable<any>
  {
    // TODO: Change this, no need to rely on client side querying as the back end is ready with paging
    return this.http.get<any>(`/documents?search=caseId:eq:${caseId}:andloadAllTasks=true&size=100&sort=createdOn,desc`);    
  }  

  public searchDocumentsByTask(taskId: number): Observable<any>
  {
    // TODO: Change this, no need to rely on client side querying as the back end is ready with paging
    return this.http.get<any>(`/documents?search=taskId:eq:${taskId}&size=100&sort=createdOn,desc`);
  }

  public searchDocumentsByAssessmentTask(assessmentTaskId: number): Observable<any>
  {
    // TODO: Change this, no need to rely on client side querying as the back end is ready with paging
    return this.http.get<any>(`/documents?search=assessmentTaskId:eq:${assessmentTaskId}&size=100&sort=createdOn,desc`);
  }  

  public searchDocumentsByMessage(messageId: number): Observable<any>
  {
    // TODO: Change this, no need to rely on client side querying as the back end is ready with paging
    return this.http.get<any>(`/documents?search=messageId:eq:${messageId}&size=100&sort=createdOn,desc`);
  }

  public searchDocumentsByMemberConsent(memberConsentId: number): Observable<any>
  {
    // TODO: Change this, no need to rely on client side querying as the back end is ready with paging
    return this.http.get<any>(`/documents?search=memberConsentId:eq:${memberConsentId}&size=100&sort=createdOn,desc`);
  }

  public uploadDocument(formData: FormData): Observable<any>
  {
    return this.http.post<any>(`/documents`, formData);
  }

  public retrieveDocument(url: string): Observable<any>
  {
    return this.http.get<any>(url);
  }

  public deleteDocument(url: string, deletionReason: string): Observable<any>
  {
    if (deletionReason !== undefined && deletionReason && deletionReason !== '')
    {
      return this.http.delete<any>(`${url}?reason=${deletionReason}`);
    }
    else
    {
      return this.http.delete<any>(`${url}`);
    }
  }

  public searchDocumentByBpmAndTaskDefinitionId(bpmId: string, definitionId: string): Observable<any>
  {
    // TODO: Change this, no need to rely on client side querying as the back end is ready with paging
    return this.http.get<any>(`/documents/bpm/${bpmId}/definition/${definitionId}`);
  }

  public searchDocumentByMessageId(id: number): Observable<any>
  {
    // TODO: Change this, no need to rely on client side querying as the back end is ready with paging
    return this.http.get<any>(`/documents/${id}/message`);
  }

  public uploadModel(formData: FormData): Observable<any>
  {
    return this.http.post<any>(`/documents/upload/model`, formData);
  }

  public sendDocument(formData: any): Observable<any>
  {
    return this.http.post<any>(`/documents/send`, formData);
  }

  public sendConsentRequest(requestData: any): Observable<any>
  {
    return this.http.post<any>(`/documents/consent/capture/send`, requestData);
  }

  public sendImageCapture(requestData: any): Observable<any>
  {
    return this.http.post<any>(`/documents/image/capture/send`, requestData);
  }

  /**
   * Verify Image Capture Link.
   *
   * @param data Image Capture Object
   */
  public verifyImageCapture(data: TokenCaptureLink): Observable<any>
  {
    return this.http.put(`/documents/image/capture/verify`, data);
  }

  /**
   * Verify Consent Capture Link.
   *
   * @param data Consent Capture Object
   */
  public verifyConsentCapture(data: TokenCaptureLink): Observable<ConsentCaptureResponse>
  {
    return this.http.put<ConsentCaptureResponse>(`/documents/consent/capture/verify`, data);
  }

  /**
   * Upload Consent Capture
   *
   * @param data Consent Capture Object
   */
  public uploadConsentCapture(data: TokenCaptureLink): Observable<any>
  {
    return this.http.put(`/documents/consent/capture/upload`, data);
  }

  public updateMemberConsentStatus(data: any): Observable<any>
  {
    return this.http.put('/documents/consent/update/status', data);
  }

  /**
   * Upload Image Capture
   *
   * @param data Image Capture Object
   */
  public uploadImageCapture(data: TokenCaptureLink): Observable<any>
  {
    return this.http.put(`/documents/image/capture/upload`, data);
  }

  /**
   * Update Image/Consent Capture Link.
   *
   * @param data Image Capture Object
   */
  public updateCaptureLink(data: TokenCaptureLink): Observable<any>
  {
    return this.http.put(`/documents/capture/update`, data);
  }

  public getReportImage(): Observable<any>
  {
    return this.http.get<any>('/documents/image/report');
  }
}
